type CodexDebugEntry = {
    detail?: string;
    href?: string;
    level: string;
    message: string;
    source: string;
    time: string;
};

type CodexDebugAccessor = {
    clear: () => void;
    read: () => CodexDebugEntry[];
    text: () => string;
};

declare global {
    interface Window {
        __ziarcadiaDebugLog?: CodexDebugAccessor;
    }
}

const CODEX_DEBUG_LOG_STORAGE_KEY = "ziarcadia:debug-log:entries";
const CODEX_DEBUG_LOG_ENABLED_KEY = "ziarcadia:debug-log";
const CODEX_DEBUG_LOG_MAX_ENTRIES = 1200;
const CODEX_DEBUG_DETAIL_MAX_LENGTH = 600;

const isBrowserDebugStorageAvailable = (): boolean =>
    typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const compactDebugText = (value: string, maxLength = CODEX_DEBUG_DETAIL_MAX_LENGTH): string =>
    value.replace(/\s+/g, " ").trim().slice(0, maxLength);

const stringifyDebugDetail = (detail: unknown): string | undefined => {
    if (detail === null || typeof detail === "undefined") {
        return undefined;
    }

    if (typeof detail === "string") {
        return compactDebugText(detail);
    }

    try {
        const seen = new WeakSet<object>();
        return compactDebugText(
            JSON.stringify(detail, (_key, value) => {
                if (typeof value === "object" && value !== null) {
                    if (seen.has(value)) {
                        return "[circular]";
                    }
                    seen.add(value);
                }
                return value;
            }) ?? String(detail)
        );
    } catch {
        return compactDebugText(String(detail));
    }
};

const readCodexDebugEntries = (): CodexDebugEntry[] => {
    if (!isBrowserDebugStorageAvailable()) {
        return [];
    }

    try {
        const raw = window.localStorage.getItem(CODEX_DEBUG_LOG_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((entry) => entry && typeof entry === "object") : [];
    } catch {
        return [];
    }
};

const writeCodexDebugEntries = (entries: CodexDebugEntry[]): void => {
    if (!isBrowserDebugStorageAvailable()) {
        return;
    }

    try {
        window.localStorage.setItem(CODEX_DEBUG_LOG_STORAGE_KEY, JSON.stringify(entries.slice(-CODEX_DEBUG_LOG_MAX_ENTRIES)));
    } catch {
        // Storage can be disabled or full; console diagnostics should still work.
    }
};

const isCodexDebugLogPersistenceEnabled = (): boolean => {
    if (!isBrowserDebugStorageAvailable()) {
        return false;
    }

    try {
        const value = window.localStorage.getItem(CODEX_DEBUG_LOG_ENABLED_KEY);
        return !["0", "false", "off", "no"].includes(String(value ?? "1").trim().toLowerCase());
    } catch {
        return true;
    }
};

export const appendCodexDebugLog = (
    source: string,
    level: string,
    message: string,
    detail?: unknown
): void => {
    if (!isCodexDebugLogPersistenceEnabled()) {
        return;
    }

    const entry: CodexDebugEntry = {
        source,
        level,
        message: compactDebugText(message, 300),
        time: new Date().toISOString()
    };
    const detailText = stringifyDebugDetail(detail);
    if (detailText) {
        entry.detail = detailText;
    }

    try {
        entry.href = window.location.href;
    } catch {
        // Ignore missing location in tests.
    }

    const entries = readCodexDebugEntries();
    entries.push(entry);
    writeCodexDebugEntries(entries);
};

const formatCodexDebugEntry = (entry: CodexDebugEntry): string => {
    const detail = entry.detail ? ` ${entry.detail}` : "";
    return `${entry.time} [${entry.source}] ${entry.level} ${entry.message}${detail}`;
};

const clearCodexDebugLog = (): void => {
    if (!isBrowserDebugStorageAvailable()) {
        return;
    }

    try {
        window.localStorage.removeItem(CODEX_DEBUG_LOG_STORAGE_KEY);
    } catch {
        // Ignore blocked storage.
    }
};

const installCodexDebugLogAccessor = (): void => {
    if (!isBrowserDebugStorageAvailable() || window.__ziarcadiaDebugLog) {
        return;
    }

    window.__ziarcadiaDebugLog = {
        clear: clearCodexDebugLog,
        read: readCodexDebugEntries,
        text: () => readCodexDebugEntries().map(formatCodexDebugEntry).join("\n")
    };
};

installCodexDebugLogAccessor();
